import React, { Fragment, useMemo } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';

// Components

import Form from '@lib/wrappers/Form';
import UniversalInput from '@lib/wrappers/UniversalInput';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import ValidationErrorMessage from '../notifications/ValidationErrorMessage';

// ActionCreators

import { changeData } from '@actions/ssap/ssapActionCreators';

// Utils

import { cloneObject } from '@utils/miscellaneous/cloneObject';
import { dataSyncingTools } from '@utils/tools/dataSyncingTools';
import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { parseFieldChangeArgs } from '@utils/ssap/parseFieldChangeArgs';
import { preventerTools } from '@utils/tools/preventerTools';
import { getDateDifference } from '@utils/dates/date.utils';

// Constants

import { DROPDOWN_OPTIONS, getDynamicDropdownNumberOption, getOptionsForHardcodedState } from '@constants/ssap/hhmTypes/dropdownOptions';
import { EMPTY_OBJECTS } from '@constants/ssap/initialValues/emptyObjects';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';
import { useExchangeAndProgramNames } from '@reducers/appReducer';
import { FOSTER_CARE_AGE_LIMIT } from '@constants/ssap/miscellaneous/miscellaneous';

/**
 *
 * @param {Object} props
 * @returns {React.Component} - 'Additional Information - Foster Care Information' Page
 *
 */
const Page2_24 = props => {
    
    const {
        data,
        data: {
            taxHousehold: {
                thCommonParams: {
                    hasSomeoneEverInFosterCare
                }
            }
        },
        environment: {
            localDates: {
                serverDate
            },
            stateCode,
            isState
        },
        form,
        formName,
        householdMembers
    } = props;
    const { exchangeMedicaid } = useExchangeAndProgramNames();
    const {
        isFormSubmitted
    } = preventerTools.provideObject(form[formName]);

    const onFieldChange_member = (...args) => {

        let { indexInArray, name, newData, value } = parseFieldChangeArgs(args);

        const newState = { ...data };
        const { dateOfBirth } = householdMembers[indexInArray];

        const {
            ageWhenLeftFosterCare,
            fosterCareState
        } = newData.specialCircumstances;

        const age = getDateDifference(serverDate, dateOfBirth, 'year');

        if(value && name === 'everInFosterCareIndicator') {
            newData.specialCircumstances = {
                ...newData.specialCircumstances,
                fosterCareState: stateCode,
                ageWhenLeftFosterCare: age > FOSTER_CARE_AGE_LIMIT ? FOSTER_CARE_AGE_LIMIT : age
            };
        }

        const ss = newState.taxHousehold.householdMember[indexInArray].specialCircumstances;
        if (!newState.taxHousehold.householdMember[indexInArray].specialCircumstances.everInFosterCareIndicator) {
            newState.taxHousehold.householdMember[indexInArray].specialCircumstances = {
                ...ss,
                ...cloneObject(EMPTY_OBJECTS.FOSTER_CARE)
            };
        } else {
            newState.taxHousehold.householdMember[indexInArray] = newData;
            if(ageWhenLeftFosterCare > 0 && fosterCareState) {
                if((age <= 25 && ageWhenLeftFosterCare === 18 && stateCode === fosterCareState) || (age <= 21 && ageWhenLeftFosterCare === 18 && stateCode!== fosterCareState)) {
                    newState.taxHousehold.householdMember[indexInArray].specialCircumstances.metFosterCareConditions = true;
                } else {
                    newState.taxHousehold.householdMember[indexInArray].specialCircumstances.metFosterCareConditions = false;
                }
            } else {
                newState.taxHousehold.householdMember[indexInArray].specialCircumstances.metFosterCareConditions = false;
            }

        }

        const syncedData = dataSyncingTools.syncThCommonProps(
            newState,
            { propName: 'foster' }
        );

        props.actions.changeData(syncedData);
    };

    const onFieldChange_none_of_above = (...args) => {

        const [ newData ] = args;

        newData.taxHousehold.householdMember = newData.taxHousehold.householdMember.map(member => {
            member.specialCircumstances = {
                ...member.specialCircumstances,
                ...cloneObject(EMPTY_OBJECTS.FOSTER_CARE)
            };
            return member;
        });

        const syncedData = dataSyncingTools.syncThCommonProps(
            newData,
            {
                isCommon: true,
                propName: 'foster'
            }
        );

        props.actions.changeData(syncedData);
    };

    const getHouseholdMembers = (householdMembers) => {
        const combinedMembers = householdMembers.map((member, i) => {
            const {
                name,
                specialCircumstances: {
                    fosterCareState,
                    everInFosterCareIndicator,
                    gettingHealthCareThroughStateMedicaidIndicator,
                    ageWhenLeftFosterCare
                },
                dateOfBirth,
                applyingForCoverageIndicator,
                gender
            } = member;

            const memberName = `${normalizeNameOfPerson(name)}`;
            const age = getDateDifference(serverDate, dateOfBirth, 'year');
            const [ stateAbbr ] = fosterCareState ? getOptionsForHardcodedState(fosterCareState) : [{}];

            return (
                <Fragment key={`${ name.firstName }_${ i }`}>
                    {applyingForCoverageIndicator ? (
                        <li>
                            <ConditionalRendering isTrue={age >= 18 && age <= 25}>
                                <UniversalInput
                                    label = { {
                                        text: 'common.input.label.non_internationalized_text',
                                        labelTextReplacements: {
                                            text: memberName
                                        }
                                    } }
                                    inputData = { {
                                        isInputRequired: false,
                                        type: INPUT_TYPES.CHECKBOX,
                                        name: 'everInFosterCareIndicator',
                                        classes: [''],
                                        checked: everInFosterCareIndicator,
                                        value: everInFosterCareIndicator,
                                        currentData: member,
                                        fields: [ 'specialCircumstances' ]
                                    } }
                                    inputActions = { {
                                        onFieldChange: onFieldChange_member
                                    } }
                                    indexInArray = { i }
                                    errorClasses = { [ 'margin-l-0' ] }
                                />
                            </ConditionalRendering>

                            <ConditionalRendering isTrue={everInFosterCareIndicator}>
                                <div className = 'margin-tb-20' >
                                    <fieldset>
                                        <legend data-testid='fosterCareState_legend'>
                                            <FormattedMessage
                                                id = 'ssap.family_household.foster_care.what_state'
                                                values = { {
                                                    personName: <strong>{ memberName }</strong>
                                                } }
                                            />
                                        </legend>
                                        <UniversalInput
                                            label = { {
                                                text: 'ssap.input.label.state',
                                                isLabelRequired: true
                                            } }
                                            inputData = { {
                                                isInputRequired: true,
                                                type: INPUT_TYPES.DROPDOWN,
                                                name: 'fosterCareState',
                                                value: fosterCareState,
                                                options: DROPDOWN_OPTIONS.STATE,
                                                validationType: VALIDATION_TYPES.CHOSEN,
                                                currentData: member,
                                                fields: ['specialCircumstances']
                                            } }
                                            errorClasses = { [ 'margin-b-0' ] }
                                            inputActions = { {
                                                onFieldChange: onFieldChange_member
                                            } }
                                            indexInArray = { i }
                                        />
                                    </fieldset>
                                    <UniversalInput
                                        legend = { {
                                            ...(isState.MN ? {
                                                dataInstructions: 'ssap.input.legend.foster_care.medicaid.learn_more',
                                            } : {}),
                                            legendText: 'ssap.input.legend.getting_health_care',
                                            legendTextReplacements: {
                                                personName: <strong>{ normalizeNameOfPerson(name) }</strong>,
                                                stateAbbr: <strong>{ stateAbbr.text }</strong>,
                                                exchangeMedicaid
                                            }
                                        } }
                                        label = { {
                                            ulClass: [ 'margin-b-20' ],
                                            showRequiredIcon: true
                                        } }
                                        inputData = { {
                                            isInputRequired: true,
                                            type: INPUT_TYPES.RADIO_BUTTONS,
                                            name: 'gettingHealthCareThroughStateMedicaidIndicator',
                                            value: gettingHealthCareThroughStateMedicaidIndicator,
                                            options: DROPDOWN_OPTIONS.YES_NO,
                                            validationType: VALIDATION_TYPES.CHOSEN,
                                            currentData: member,
                                            fields: [ 'specialCircumstances' ]
                                        } }
                                        inputActions = { {
                                            onFieldChange: onFieldChange_member
                                        } }
                                        indexInArray = { i }
                                    />
                                    <fieldset>
                                        <legend data-testid='ageWhenLeftFosterCare_legend'>
                                            <FormattedMessage
                                                id = 'ssap.family_household.foster_care.how_old'
                                                values = { {
                                                    personName: <strong>{ memberName }</strong>,
                                                    gender
                                                } }
                                                tagName = 'legend'
                                            />
                                        </legend>
                                        <UniversalInput
                                            label = { {
                                                text: 'ssap.input.label.age',
                                                isLabelRequired: true
                                            } }
                                            inputData = { {
                                                isInputRequired: true,
                                                type: INPUT_TYPES.DROPDOWN,
                                                name: 'ageWhenLeftFosterCare',
                                                value: ageWhenLeftFosterCare,
                                                options: getDynamicDropdownNumberOption(age, FOSTER_CARE_AGE_LIMIT),
                                                validationType: VALIDATION_TYPES.CHOSEN,
                                                currentData: member,
                                                handleAsNumbers: true,
                                                fields: [ 'specialCircumstances' ]
                                            } }
                                            errorClasses = { [ 'margin-b-0' ] }
                                            inputActions = { {
                                                onFieldChange: onFieldChange_member
                                            } }
                                            indexInArray = { i }
                                        />
                                    </fieldset>
                                </div>
                            </ConditionalRendering>
                        </li>
                    ) : null}
                </Fragment>
            );

        });

        return (
            <ul className = 'usa-list usa-list--unstyled' >
                {combinedMembers}

                <li key = 'hasSomeoneEverInFosterCare'>
                    <UniversalInput
                        label = { {
                            text: 'ssap.input.label.none_of_above',
                            isLabelRequired: false
                        } }
                        inputData = { {
                            isInputRequired: false,
                            type: INPUT_TYPES.CHECKBOX,
                            name: 'hasSomeoneEverInFosterCare',
                            checked: hasSomeoneEverInFosterCare === false,
                            value: hasSomeoneEverInFosterCare === false,
                            currentData: cloneObject(data),
                            fields: [ 'taxHousehold', 'thCommonParams' ],
                            reversedValue: true
                        } }
                        inputActions = { {
                            onFieldChange: onFieldChange_none_of_above
                        } }
                    />
                </li>
            </ul>
        );
    };

    const topLevelValidity = useMemo(
        () => {
            return householdMembers.some(hhm => hhm.specialCircumstances.everInFosterCareIndicator)
                || hasSomeoneEverInFosterCare === false;
        },
        [ householdMembers, hasSomeoneEverInFosterCare ]
    );

    return (
        <Fragment>
            <div
                className = 'fade-in'
                id = 'fhMoreInfo'
            >
                <div className = 'subsection' >
                    <Form
                        { ...{
                            formName,
                            topLevelValidity
                        } }
                    >
                        <fieldset>
                            <legend data-testid='foster_care_details_legend'>
                                <p className = 'required-true' data-testid = 'foster_care_question' >
                                    <FormattedMessage id = 'ssap.family_household.foster_care.was_anybody_on_foster_care' />
                                </p>
                                <ValidationErrorMessage
                                    isVisible = { isFormSubmitted && !topLevelValidity }
                                    messageText = { VALIDATION_ERROR_MESSAGES.ANY_VALUE }
                                    errorClasses = { [ 'margin-l-0' ] }
                                />
                            </legend>
                            {
                                getHouseholdMembers(householdMembers)
                            }
                        </fieldset>
                    </Form>
                </div>
            </div>
        </Fragment>
    );
};

const mapStateToProps = state => ({
    environment: state.common.environment,

    data: state.ssap.data,
    form: state.ssap.form,

    householdMembers: state
        .ssap
        .data
        .taxHousehold
        .householdMember
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        changeData
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Page2_24);
